import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import AddProductModal from '../components/PlanCosecha/AddProductModal';
import EditProductModal from '../components/PlanCosecha/EditProductModal';
import BulkUploadModal from '../components/PlanCosecha/BulkUploadModal';
import { Can } from '../components/Auth/Can';
import {
  selectSeason,
  selectAvailableSeasons,
} from '../store/slices/seasonSlice';
import { fetchProducts, selectAllProducts, selectProductsLoading } from '../store/slices/productsSlice';
import {
  fetchPlanCosecha,
  createPlanCosecha,
  savePlanCosecha,
  deletePlanCosecha,
  selectPlanCosecha,
  selectPlanCosechaLoading,
  selectPlanCosechaCreating,
  selectPlanCosechaSaving,
  selectPlanCosechaDeleting,
  selectPlanCosechaError,
} from '../store/slices/planCosechaSlice';

const PlanCosecha = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedSeason = useSelector(selectSeason);
  const seasons = useSelector(selectAvailableSeasons);
  const plan = useSelector(selectPlanCosecha);
  const loading = useSelector(selectPlanCosechaLoading);
  const creating = useSelector(selectPlanCosechaCreating);
  const saving = useSelector(selectPlanCosechaSaving);
  const deleting = useSelector(selectPlanCosechaDeleting);
  const error = useSelector(selectPlanCosechaError);
  const productsFromStore = useSelector(selectAllProducts);
  const products = useMemo(() => productsFromStore || [], [productsFromStore]);
  const productsLoading = useSelector(selectProductsLoading);
  const [rows, setRows] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [removedDetailIds, setRemovedDetailIds] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);

  useEffect(() => {
    if (plan?.detalles) {
      setRows(plan.detalles);
      setHasChanges(false);
      setRemovedDetailIds([]);
    } else {
      setRows([]);
      setHasChanges(false);
      setRemovedDetailIds([]);
    }
  }, [plan]);

  // Los datos de stock y pedidos ahora vienen directamente de la vista
  // No necesitamos hacer fetch adicionales

  useEffect(() => {
    if (!products.length && !productsLoading) {
      dispatch(fetchProducts());
    }
  }, [dispatch, products.length, productsLoading]);

  useEffect(() => {
    if (!selectedSeason) {
      return;
    }

    const temporada = seasons.find((item) => item.anio === selectedSeason.year);
    const temporadaId = temporada?.id;

    if (temporadaId) {
      dispatch(fetchPlanCosecha({ temporadaId, week: selectedSeason.week }));
    }
  }, [dispatch, selectedSeason, seasons]);

  const availableProducts = useMemo(() => {
    const selectedIds = new Set(rows.map((row) => row.producto_id));
    return products.filter((product) => !selectedIds.has(product.id));
  }, [products, rows]);

  const handleCreatePlan = () => {
    if (!selectedSeason) return;

    const temporada = seasons.find((item) => item.anio === selectedSeason.year);
    if (!temporada) return;

    dispatch(
      createPlanCosecha({
        temporadaId: temporada.id,
        seasonYear: selectedSeason.year,
        week: selectedSeason.week,
      })
    );
  };

  const handleAddProduct = (producto_id, cantidad_planificada) => {
    const productItem = products.find((item) => item.id === producto_id);
    if (!productItem) return;

    setRows((prev) => [
      ...prev,
      {
        id: null,
        producto_id: productItem.id,
        cantidad_pedidos: 0,
        cantidad_planificada: cantidad_planificada,
        cantidad_real: null,
        stock_actual: 0,
        pedidos_semana: 0,
        cantidad_pendiente: 0,
        producto: productItem,
      },
    ]);
    setHasChanges(true);
  };

  const handleEditProduct = (index) => {
    setSelectedProduct(rows[index]);
    setSelectedProductIndex(index);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = (cantidad_planificada) => {
    if (selectedProductIndex === null) return;

    setRows((prev) => {
      const updated = [...prev];
      updated[selectedProductIndex] = {
        ...updated[selectedProductIndex],
        cantidad_planificada: cantidad_planificada
      };
      return updated;
    });

    setHasChanges(true);
    setSelectedProduct(null);
    setSelectedProductIndex(null);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => {
      const updated = [...prev];
      const removed = updated[index];
      
      // Si el detalle tiene ID, agregarlo a la lista de eliminados
      if (removed.id) {
        setRemovedDetailIds((prevIds) => [...prevIds, removed.id]);
      }
      
      updated.splice(index, 1);
      return updated;
    });
    setHasChanges(true);
  };


  const handleSavePlan = async () => {
    if (!plan || !hasChanges) return;

    await dispatch(savePlanCosecha({
      planId: plan.id,
      detalles: rows,
      removedDetailIds: removedDetailIds
    }));
  };

  const handleBulkUploadSuccess = () => {
    // Refrescar el plan después de la carga masiva
    const temporada = seasons.find((item) => item.anio === selectedSeason.year);
    const temporadaId = temporada?.id;
    
    if (temporadaId) {
      dispatch(fetchPlanCosecha({ temporadaId, week: selectedSeason.week }));
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedSeason) return;

    const temporada = seasons.find((item) => item.anio === selectedSeason.year);
    if (!temporada) return;

    await dispatch(deletePlanCosecha({
      temporadaId: temporada.id,
      semana: selectedSeason.week
    }));
    setIsDeleteModalOpen(false);
  };




  const renderContent = () => {
    if (!selectedSeason) {
      return (
        <div className="empty-state">
          <p>{t('planProduction.noSeasonSelected')}</p>
        </div>
      );
    }

    const temporada = seasons.find((item) => item.anio === selectedSeason.year);
    const temporadaId = temporada?.id;

    if (!temporadaId) {
      return (
        <div className="empty-state">
          <p>{t('planProduction.noPlan')}</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="loading-state">
          <p>{t('common.loading')}</p>
        </div>
      );
    }

    if (!plan) {
      return (
        <div className="empty-state">
          <p>{t('planProduction.noPlan')}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreatePlan}
            disabled={creating}
          >
            {creating ? t('common.loading') : t('planProduction.createButton')}
          </button>
        </div>
      );
    }

    return (
      <div className="plan-cosecha">
        <div className="plan-header">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('planProduction.title')}</h1>
            <p className="text-lg text-gray-600 mt-2">{t('planProduction.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {hasChanges && (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSavePlan}
                disabled={saving}
              >
                {saving ? t('common.loading') : t('common.save')}
              </button>
            )}
            <Can I="delete" this="production:plan">
              <button
                type="button"
                className="btn btn-outline btn-delete-plan"
                onClick={() => setIsDeleteModalOpen(true)}
                title="Borrar plan de cosecha"
              >
                <Trash2 size={18} />
              </button>
            </Can>
            <Can I="bulk" this="production:plan">
              <button
                type="button"
                className="btn btn-outline btn-bulk-upload"
                onClick={() => setIsBulkUploadModalOpen(true)}
                title="Carga masiva desde archivo Excel"
              >
                <Upload size={18} />
              </button>
            </Can>
            <Can I="create" this="production:plan">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setIsAddModalOpen(true)}
              >
                <Plus size={20} />
                {t('planProduction.addProduct')}
              </button>
            </Can>
          </div>
        </div>


        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="table-container">
          {rows.length === 0 ? (
            <div className="empty-state">
              <p>{t('planProduction.emptyRows')}</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('planProduction.table.product')}</th>
                  <th>{t('planProduction.table.inventarioCampo')}</th>
                  <th>{t('planProduction.table.inventarioWarehouse')}</th>
                  <th>{t('planProduction.table.orders')}</th>
                  <th>{t('planProduction.table.planned')}</th>
                  <th>{t('planProduction.table.faltante')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  // Los valores ahora vienen directamente de la vista
                  const stockCampo = row.stock_campo ?? 0;
                  const stockWarehouse = row.stock_warehouse ?? 0;
                  const pedidos = row.pedidos_semana ?? row.cantidad_pedidos ?? 0;
                  const faltante = pedidos - stockCampo - stockWarehouse;
                  
                  return (
                    <tr key={`${row.producto_id}-${index}`}>
                      <td>
                        <div className="product-info">
                          <span className="product-name">{row.producto?.nombre || row.producto_id}</span>
                          {row.producto?.code && (
                            <span className="product-code">{row.producto.code}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span>{stockCampo}</span>
                      </td>
                      <td>
                        <span>{stockWarehouse}</span>
                      </td>
                      <td>
                        <span>{pedidos}</span>
                      </td>
                      <td>
                        <span>{row.cantidad_planificada ?? 0}</span>
                      </td>
                      <td>
                        <span className="faltante-positive">
                          {faltante > 0 ? faltante : ''}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Can I="update" this="production:plan">
                            <button
                              className="btn-icon btn-icon-edit"
                              onClick={() => handleEditProduct(index)}
                              title={t('common.edit')}
                            >
                              <Edit2 size={18} />
                            </button>
                          </Can>
                          <Can I="delete" this="production:plan">
                            <button
                              className="btn-icon btn-icon-delete"
                              onClick={() => handleRemoveRow(index)}
                              title={t('common.delete')}
                            >
                              <Trash2 size={18} />
                            </button>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <AddProductModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddProduct}
          availableProducts={availableProducts}
        />

        <EditProductModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedProduct(null);
            setSelectedProductIndex(null);
          }}
          onSave={handleSaveEdit}
          productItem={selectedProduct}
        />

        <BulkUploadModal
          isOpen={isBulkUploadModalOpen}
          onClose={() => setIsBulkUploadModalOpen(false)}
          onSuccess={handleBulkUploadSuccess}
          temporadaId={temporada?.id}
          semana={selectedSeason?.week}
        />

        {isDeleteModalOpen && (
          <div className="modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
            <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="delete-modal-header">
                <h3>Confirmar eliminación</h3>
              </div>
              <div className="delete-modal-body">
                <p>¿Está seguro que desea eliminar el plan de cosecha de la semana {selectedSeason?.week} de la temporada {selectedSeason?.year}?</p>
                <p className="warning-text">Esta acción no se puede deshacer.</p>
              </div>
              <div className="delete-modal-footer">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeletePlan}
                  disabled={deleting}
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          .plan-header {
            display: flex;
            flex-direction: row !important;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: var(--spacing-6);
            gap: var(--spacing-4);
          }

          .plan-header > div:first-child {
            flex: 1;
          }

          .plan-header > button {
            flex-shrink: 0;
          }

          .action-buttons {
            display: flex;
            gap: var(--spacing-2);
            align-items: center;
          }

          .btn-bulk-upload,
          .btn-delete-plan {
            padding: var(--spacing-2) var(--spacing-3);
            min-width: auto;
          }

          .btn-bulk-upload svg,
          .btn-delete-plan svg {
            margin: 0;
          }

          .btn-delete-plan {
            border-color: #DC2626;
            color: #DC2626;
          }

          .btn-delete-plan:hover {
            background-color: #DC2626;
            color: white;
          }

          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .delete-modal-content {
            background: white;
            border-radius: 12px;
            max-width: 420px;
            width: 90%;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            overflow: hidden;
          }

          .delete-modal-header {
            padding: 20px 24px 0 24px;
          }

          .delete-modal-header h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #111827;
          }

          .delete-modal-body {
            padding: 16px 24px;
          }

          .delete-modal-body p {
            margin: 0 0 12px 0;
            color: #4B5563;
            font-size: 14px;
            line-height: 1.5;
          }

          .delete-modal-body .warning-text {
            color: #DC2626;
            font-weight: 500;
            margin-bottom: 0;
          }

          .delete-modal-footer {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            padding: 16px 24px 20px 24px;
            background: #F9FAFB;
            border-top: 1px solid #E5E7EB;
          }

          .btn-danger {
            background-color: #DC2626;
            color: white;
            border: none;
          }

          .btn-danger:hover {
            background-color: #B91C1C;
          }

          .btn-danger:disabled {
            background-color: #FCA5A5;
            cursor: not-allowed;
          }

          .btn-icon {
            background: none;
            border: none;
            padding: var(--spacing-2);
            border-radius: var(--radius-md);
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .btn-icon-edit {
            color: var(--primary-blue);
          }

          .btn-icon-edit:hover {
            background-color: rgba(59, 130, 246, 0.1);
          }

          .btn-icon-delete {
            color: #DC2626;
          }

          .btn-icon-delete:hover {
            background-color: rgba(220, 38, 38, 0.1);
          }

          .product-info {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
          }

          .product-name {
            font-weight: 600;
            color: var(--gray-900);
          }

          .product-code {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
          }

          .faltante-positive {
            color: #DC2626;
            font-weight: 600;
          }

          @media (max-width: 768px) {
            .plan-header {
              flex-direction: column;
              gap: var(--spacing-4);
            }
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="plan-cosecha-page">
      {renderContent()}
    </div>
  );
};

export default PlanCosecha;
